import {
  normalizeRequestError,
  REQUEST_OUTCOMES,
  requestTransport,
} from './helpers/requestTransport';
import redirectToLogin from './helpers/redirectToLogin';
import {
  chromeViewerRouteMatches,
  tokenizedOwnershipMatches,
} from '../utils/chromeViewerRequestOwnership';

const REQUEST_TYPES = {
  begin: 'BEGIN_GET_SINGLE_UPLOADED_RECORD',
  fail: 'FAILED_GET_SINGLE_UPLOADED_RECORD',
  end: 'FINISHED_GET_SINGLE_UPLOADED_RECORD',
};
const SAVE_TYPES = {
  begin: 'BEGIN_SAVE_ANNOTATIONS_ON_RECORD',
  fail: 'FAILED_SAVE_ANNOTATIONS_ON_RECORD',
  end: 'FINISHED_SAVE_ANNOTATIONS_ON_RECORD',
};
let selectionSaveAttemptSequence = 0;

const cancellationResult = () => ({ canceled: true, cancelled: true });
const unexpectedFailure = () => ({
  outcome: REQUEST_OUTCOMES.failure,
  value: {
    response: {
      status: undefined,
      data: { error: 'An unexpected error occurred, please try again.' },
    },
  },
});

const request = async (options, callbacks = {}) => {
  try {
    return await requestTransport(options, {
      ...callbacks,
      normalizeError: (error) => normalizeRequestError(error, {
        fallbackStatus: null,
        repairIncompleteResponse: false,
      }),
    });
  } catch {
    return unexpectedFailure();
  }
};

const requestResult = (transportResult) => {
  if (transportResult.outcome === REQUEST_OUTCOMES.cancelled) {
    return cancellationResult();
  }
  if (transportResult.outcome === REQUEST_OUTCOMES.failure) {
    return { ...transportResult.value, error: true };
  }
  return { ...transportResult.value, success: true };
};

const reduxRequestCallbacks = (dispatch) => ({
  onRetry: (retries) => dispatch({
    type: 'RETRYING_REQUEST',
    payload: { retries },
  }),
  onRetryFailed: () => dispatch({ type: 'RETRYING_FAILED' }),
  onUnauthorized: (error) => redirectToLogin(dispatch, error),
});

const responseOnlyRequestCallbacks = {
  onUnauthorized: (error) => redirectToLogin(undefined, error),
};

const normalizeSuccessfulRecordData = (result) => {
  if (!result?.success || (result.data && typeof result.data === 'object'
    && !Array.isArray(result.data))) {
    return result;
  }

  return {
    ...result,
    data: {},
  };
};

const ownsActiveRoute = (getState, ownership, { requireUnsettled = true } = {}) => {
  const state = getState();
  const activeRequest = state.records.currentRecordRequest;

  return tokenizedOwnershipMatches(activeRequest, ownership)
    && (!requireUnsettled || !activeRequest.settled)
    && chromeViewerRouteMatches(state.router.location.pathname, ownership.routeOwnerId);
};

const terminalMeta = (getState, ownership, extraMeta = {}) => ({
  requestOwnership: ownership,
  routePathname: getState().router.location.pathname,
  ...extraMeta,
});

const dispatchTerminal = ({
  dispatch,
  getState,
  ownership,
  payload,
  type,
  extraMeta,
  requireUnsettled = true,
  shouldDispatch,
  onDispatched,
}) => {
  if (!ownsActiveRoute(getState, ownership, { requireUnsettled })) return false;
  if (shouldDispatch && !shouldDispatch()) return false;

  dispatch({
    type,
    payload,
    meta: terminalMeta(getState, ownership, extraMeta),
  });
  onDispatched?.(type);
  return true;
};

const selectionSaveOwnership = (ownership) => {
  selectionSaveAttemptSequence += 1;
  return {
    ...ownership,
    kind: 'selection-save',
    selectionSaveAttemptToken: `${ownership.attemptToken}:save:${selectionSaveAttemptSequence}`,
  };
};

const loadChromeViewerRecord = (id, { requestOwnership, signal }) => async (dispatch, getState) => {
  dispatch({
    type: REQUEST_TYPES.begin,
    meta: { requestOwnership },
  });

  const transportResult = await request({
    method: 'GET',
    url: `/api/v1/records/${id}?sent=false&ext=false&archive=false`,
    signal,
  }, reduxRequestCallbacks(dispatch));
  const result = normalizeSuccessfulRecordData(requestResult(transportResult));

  if (transportResult.outcome === REQUEST_OUTCOMES.cancelled) {
    dispatchTerminal({
      dispatch,
      getState,
      ownership: requestOwnership,
      type: REQUEST_TYPES.fail,
      payload: {},
      extraMeta: { cancelled: true },
    });
    return result;
  }

  dispatchTerminal({
    dispatch,
    getState,
    ownership: requestOwnership,
    type: result.error ? REQUEST_TYPES.fail : REQUEST_TYPES.end,
    payload: result,
  });
  return result;
};

const loadChromeViewerSelection = (id, {
  archive = false,
  signal,
} = {}) => async () => (
  request({
    method: 'GET',
    url: `/api/v1/records/${id}?sent=false&ext=true&archive=${archive}`,
    signal,
  }, responseOnlyRequestCallbacks).then(requestResult)
);

const saveChromeViewerAnnotations = (record, id, {
  onTerminalDispatched,
  ownership,
  shouldDispatchTerminal,
}) => async (dispatch, getState) => {
  const saveOwnership = selectionSaveOwnership(ownership);
  dispatch({
    type: SAVE_TYPES.begin,
    meta: { requestOwnership: saveOwnership },
  });

  const transportResult = await request({
    method: 'POST',
    url: `/api/v1/records/${id}/create_edited_record`,
    data: record,
  }, reduxRequestCallbacks(dispatch));
  const result = requestResult(transportResult);
  const cancelled = transportResult.outcome === REQUEST_OUTCOMES.cancelled;

  dispatchTerminal({
    dispatch,
    getState,
    ownership: saveOwnership,
    type: transportResult.outcome === REQUEST_OUTCOMES.success
      ? SAVE_TYPES.end
      : SAVE_TYPES.fail,
    payload: cancelled ? {} : result,
    extraMeta: {
      selectionSaveGatePassed: true,
      ...(cancelled ? { cancelled: true } : {}),
    },
    requireUnsettled: false,
    shouldDispatch: shouldDispatchTerminal,
    onDispatched: onTerminalDispatched,
  });
  return result;
};

export {
  loadChromeViewerRecord,
  loadChromeViewerSelection,
  saveChromeViewerAnnotations,
};
